/*
	ai_transports.ts
  Copyright (c) 2013-present Human Cube Inc.

  TODO:  
      - Does not do SAME ZONE loading/unloading which will be needed for paratroops.
*/

import BF from '../bfcore/bfconst1';
import bfH from '../bfcore/bf_helper';
import { getNewZoneXY } from '../bfcore/bf_hitmap';
import { Game, PlayerMoves } from '../bfcore/gameTypes';
import { WorkingData } from '../bfworking/workingTypes';
import { forceIndexMatchingForceID } from '../bfworking/workingForceIndex';



function findCurrentCargoTotal (working: WorkingData, game: Game, forceID: number): number {
  // Returns number of cargo points currently used up on a transport type force.
  let total = 0;
  const desiredCargoZoneID = BF.FORCE_ZONE_CARGO - forceID;
  const forcesCount = working.force.length;
  const forces = working.force;
  for (let j = 0; j < forcesCount; j++) {
    if (forces[j].z === desiredCargoZoneID) {
      const cargoForceTypeIndex = forces[j].ft;
      const cargoSpace = game.forceCargoSpace[cargoForceTypeIndex];
      total += cargoSpace;
    }
  }
  return total;
}

function findPotentialCargo (
  working: WorkingData,
  game: any,
  playerIndex: number,
  zoneID: number,
  capacity: number,
): { cargoIndex: number, cargoSize: number } {
  // Return an index to a force for the player that can load for the given zoneID and capacity.
  // Return -1 if none available.
  var troopTypeIndex = bfH.getForceTypeIndexByName(game, 'Troop');
  var tankTypeIndex = bfH.getForceTypeIndexByName(game, 'Tank');

  const forcesCount = working.force.length;
  const forces = working.force;
  for (let j = 0; j < forcesCount; j++) {
    const force = forces[j];
    if(force.owner !== playerIndex) {
      continue;
    }
    if(force.z <= 0 || force.move.length !== 0 || force.r === 0) {
      continue;  // Is destroyed, already moved or is blocked.
    }
    const forceTypeIndex = force.ft;
    if(forceTypeIndex === troopTypeIndex || forceTypeIndex === tankTypeIndex) {
      const cargoSize = game.forceCargoSpace[forceTypeIndex];
      if(cargoSize <= capacity) {
        if (bfH.isNeighbour(game, force.z, zoneID)) {
          return { cargoIndex: j, cargoSize: cargoSize };
        }
      }
    }
  }
  return { cargoIndex: -1, cargoSize: 0 };
}

export function doTransportLoading (move: PlayerMoves, working: WorkingData, game: Game): void {
  // Adds moves to the move parameter.
  // CAREFUL NOT TO MODIFY VALUES IN game, AS IT WILL CORRUPT IT (It might be the source, not a copy).
  // working is cloned and can be modified if desired.

  // move: {
  //        forceID3: [
  //             zoneID to move to 1, x to move to 1, y to move to 1, extra 1,
  //             zoneID to move to 2, x to move to 2, y to move to 2, extra 2],
  //         ...
  //       },
  //   WHERE:  extra is 0 for normal movement
  //           extra is -1 for load cargo (zoneID is the force ID of cargo to load)
  //           extra is forceID of cargo to unload (zoneID, x, y is for destination of cargo)

  // Generates movement for a player (specified by working.localPlayerIndex).
  // Returns an dictionary of force moves.

  const moveStage = working.ai.moveStage;
  if (moveStage < 0) {
    return;  // AI as done all the move stages, so do nothing.
  }

  const playerIndex = working.localPlayerIndex;

  const forcesCount = working.force.length;
  const forces = working.force;

  // Potentially load all possible transports with one force if they can move:
  for (let j = 0; j < forcesCount; j++) {
    let force = forces[j];
    if (force.owner !== playerIndex) {
      continue;  // Not this players force.
    }

    if (force.move.length !== moveStage) {
      // Force has already moved in this stage or has not moved in the previous move stage.
      // So do not move it any more.
      continue;
    }

    const forceID = force.id;
    const forceTypeIndex = force.ft;
    // const forceFlags = game.forceFlags[forceTypeIndex];
    const capacity = game.forceCargoCapacity[forceTypeIndex];
    const zoneID = force.z;

    if (zoneID <= 0 || zoneID > game.zones) {
      // It is either already cargo or destroyed, so do not gererate a move for it.
      continue;
    }

    if(capacity <= 0) {
      continue;  // It is not a transport of any sort.
    }

    const currentCargoAmount = findCurrentCargoTotal(working, game, forceID);
    if(capacity <= currentCargoAmount) {
      continue;
    }

    const {cargoIndex, cargoSize} = findPotentialCargo(
      working, game, playerIndex, zoneID, capacity - currentCargoAmount,
    );
    if (cargoIndex >= 0) {
      if (!move[forceID])
        move[forceID] = [];
      const cargoForceID = forces[cargoIndex].id;
      move[forceID].push(cargoForceID, 0, 0, -1);
      // Also update working data so it is respected properly in this turn generation:
      force.move.push({ z: cargoForceID, x: 0, y: 0, e: -1 });
      force.lp -= cargoSize;
      forces[cargoIndex].z = BF.FORCE_ZONE_CARGO - forceID;  // Update cargo location as loaded.
      forces[cargoIndex].x = 0;
      forces[cargoIndex].y = 0;
      forces[cargoIndex].r = 0;  // Loaded cargo consumes all range.
    }
  }
}

export function doTransportUnloading (move: PlayerMoves, working: WorkingData, game: Game): void {

  const moveStage = working.ai.moveStage;
  if (moveStage < 0) {
    return;  // AI as done all the move stages, so do nothing.
  }

  const playerIndex = working.localPlayerIndex;

  const forcesCount = working.force.length;
  const forces = working.force;

  // Potentially unload cargo from all possible transports if they have stopped moving:
  for (let j = 0; j < forcesCount; j++) {
    let force = forces[j];
    if (force.owner !== playerIndex) {
      continue;  // Not this players force.
    }

    const forceID = force.id;
    // const forceTypeIndex = force.ts;
    const zoneID = force.z;
    if (zoneID > BF.FORCE_ZONE_CARGO) {
      continue;  // It is not cargo.
    }

    // It is cargo, attempt to unload it.
    const transportID = BF.FORCE_ZONE_CARGO - zoneID;
    const transportIndex = forceIndexMatchingForceID(working, transportID);

    if (forces[transportIndex].move.length !== moveStage) {
      // The transport Force has already moved in this stage or has not moved in the previous move
      // stage. So do not move it any more.
      continue;
    }

    if (transportIndex >= 0) {
      const transportZoneID: number = forces[transportIndex].z;
      const moveToZoneID: number = bfH.randomLandNeighbour(game, transportZoneID);
      if (moveToZoneID > 0) {
        // Do the cargo unload:
        const pos = getNewZoneXY(game, moveToZoneID);
        if (!move[transportID])
          move[transportID] = [];
        move[transportID].push(moveToZoneID, pos.x, pos.y, forceID);
        // Also update working data so it is respected properly in this turn generation:
        force.z = moveToZoneID;
        force.x = pos.x;
        force.y = pos.y;
        force.r = 0;  // Cargo cannot move after being unloaded.
        forces[transportIndex].move.push({ z: moveToZoneID, x: pos.x, y: pos.y, e: forceID });
        forces[transportIndex].r = 0;  // Prevent transport from moving any more this turn.
      }
    }
  }
}
